import {CGP_EVENTS_NAMES, CGPEventSubscription, ICgpSdk} from './cgp-sdk.interface';
import { LiveTables } from '../live-table-feeds-service/live-table-feeds.service.interface';
import {TimeSpan} from '@sparkware/app-utils';
import {IBlueRibbonPartyJackpot} from '../../../common/interfaces/blue-ribbon-party-jackpot.interface';

export interface OnShowGamePopupCallbackParams {
  gameType?: number,
  gamePopupApprovedCallback: () => void
}

export interface OnGameOpenParams {
  gameType: number;
  isReal: boolean;
}

export interface OnGameCloseParams {
  gameType: number;
  isReal: boolean;
  isGamePlayed: boolean;
  showNextGameSuggestion: boolean;
}

export interface OnPlayerActionParams {
  actionName: string;
  options?: any;
}

export interface OnOauthStateChangedParams {
  loginType?: number;
  logoutReason?: number;
  kickReason?: number;
}

export interface OnBlueRibbonIsEnabledChangedParams {
  isEnabled: boolean;
  isAllLiveGamesPromotion: boolean;
}

export class SdkEvents {
  constructor(private CGP: ICgpSdk) {

  }

  onOAuthStateChanged(callback: (params: OnOauthStateChangedParams) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.OAUTH_STATE_CHANGE,
      callback: callback,
      context: this
    });
  }

  onBeforeOAuthStateChanged(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.ON_BEFORE_OAUTH_STATE_CHANGE,
      callback: callback,
      context: this
    });
  }

  onDeepLinkReceived(callback: (deepLinkObject: any) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.DEEPLINK_RECEIVED,
      callback: callback,
      context: this
    });
  }

  onShowGamePopup(callback: (args: OnShowGamePopupCallbackParams) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.GAME_POPUP_SHOW,
      callback: callback,
      context: this
    });
  }

  onGameOpen(callback: (params: OnGameOpenParams) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.GAME_OPEN,
      callback: callback,
      context: this
    });
  }

  onGameClose(callback: (params?: OnGameCloseParams) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.CLOSE_GAME,
      callback: callback,
      context: this
    });
  }


  onPlayerAction(callback: (eventData: OnPlayerActionParams) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.PLAYER_ACTION,
      callback: (data) => callback(data || {} as OnPlayerActionParams),
      context: this
    });
  }

  onRecentlyPlayedGamesUpdated(callback: (gameTypes: number[]) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.RECENTLY_PLAYED_GAMES_UPDATED,
      callback: callback,
      context: this
    });
  }

  onLoginFormOpen(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.LOGIN_FORM_OPEN,
      callback: callback,
      context: this
    });
  }

  onLoginFormClosed(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.LOGIN_FORM_CLOSE,
      callback: callback,
      context: this
    });
  }

  onTokenLifetimeExpired(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.TOKEN_LIFETIME_EXPIRED,
      callback: callback,
      context: this
    });
  }

  onLiveTableFeedsUpdate(callback: (tablesData: LiveTables) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.LIVE_TABLES_UPDATE,
      callback: callback,
      context: this
    });
  }

  onGamesQuestionnaireCompleted(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.GAMES_QUESTIONNAIRE_COMPLETED,
      callback: callback,
      context: this
    });
  }

  onFreeplayVouchersUpdated(callback: (vouchers: []) => void):CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.FREE_PLAY_UPDATED,
      callback: callback,
      context: this
    })
  }

  onJackpotsWithStateUpdate(callback: (jackpots: unknown) => void) {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.LIVE_FEEDS_STATE_JACKPOTS_UPDATED,
      callback,
      context: this
    });
  }

  onJackpotsAmountsUpdate(callback: (jackpots: Record<string, unknown>) => void) {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.LIVE_FEEDS_JACKPOT_AMOUNTS_UPDATED,
      callback,
      context: this
    });
  }

  onBlueRibbonPartyJackpotStateChanged(callback: (partyJackpot: IBlueRibbonPartyJackpot) => void) {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.BLUE_RIBBON_PARTY_JP_STATE_CHANGED,
      callback,
      context: this
    });
  }

  onBlueRibbonPartyJackpotWin(callback: (partyJackpot: IBlueRibbonPartyJackpot) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.BLUE_RIBBON_PARTY_JACKPOT_WIN,
      callback: callback,
      context: this
    });
  }

  onBlueRibbonPartyJackpotInsufficientMoney(callback: (partyJackpot: IBlueRibbonPartyJackpot) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.BLUE_RIBBON_PARTY_JACKPOT_INSUFFICIENT_MONEY,
      callback: callback,
      context: this
    });
  }

  onBlueRibbonConnectionComplete(callback: (partyJackpot: IBlueRibbonPartyJackpot) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.BLUE_RIBBON_CONNECTION_COMPLETE,
      callback: callback,
      context: this
    });
  }

  onBlueRibbonIsEnabledChanged(callback: (params: OnBlueRibbonIsEnabledChangedParams) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.BLUE_RIBBON_IS_ENABLED_CHANGED,
      callback: callback,
      context: this
    });
  }

  onBalanceUpdated(callback: () => void) {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.BALANCE_UPDATED,
      callback,
      context: this
    });
  }

  onSessionTimerUpdate(callback: (data: {sessionTime: TimeSpan}) => void) {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.SESSION_TIMER_UPDATE,
      callback,
      context: this
    });
  }

  onRealityCheckOpened(callback: () => void) {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.REALITY_CHECK_OPENED,
      callback,
      context: this
    });
  }

  onRealityCheckClosed(callback: () => void) {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.REALITY_CHECK_CLOSED,
      callback,
      context: this
    });
  }

  onDailyDealRemoveIndication(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.DAILY_DEAL_REMOVE_INDICATION,
      callback: callback,
      context: this
    });
  }

  onRegistrationCompleted(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.REGISTRATION_COMPLETED,
      callback,
      context: this
    });
  }

  onOmgAwaitingMessagesUpdated(callback: (numberOfAwaitingMessages: number) => void) {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.OMG_AWAITING_MESSAGES_UPDATED,
      callback,
      context: this
    });
  }

  onAppResumed(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.APP_RESUMED,
      callback: callback,
      context: this
    });
  }

  onAppPaused(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.APP_PAUSED,
      callback: callback,
      context: this
    });
  }

  onExternalLinkClosed(callback: (linkSource: string) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.EXTERNAL_LINK_CLOSED,
      callback: callback,
      context: this
    });
  }

  onTouchIDActiveStateChanged(callback: (state: boolean) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.TOUCH_ID_STATE_CHANGED,
      callback: callback,
      context: this
    });
  }

  onOmgDisplayed(callback: (state: unknown) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.OMG_DISPLAYED,
      callback,
      context: this
    });
  }

  onBurnoutMessageReceived(callback: (state: unknown) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.SESSION_EVENT_PLAYER_BURNOUT_MESSAGE_RECEIVED,
      callback,
      context: this
    });
  }

  onLiveGameSwitched(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.LIVE_GAME_SWITCHED,
      callback: callback,
      context: this
    });
  }

  onPromotionsChanged(callback: (data: {Cid: number, IsPromotionDisplayedInClient: boolean, IndicationInClientPromotionsIds: number[]}) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.PROMOTIONS_CHANGED,
      callback: callback,
      context: this
    });
  }
}
